import { useAppDispatch, useAppSelector } from "./redux";
import {
  amplifyProvider,
  authController,
  circadiaEngineProvider,
  clinicController,
  homeHealthController,
  localStorageProvider,
  notificationController,
  patientController,
  smsController,
  summaryController,
  timeProvider,
  userController,
  watchlistController,
} from "../AppController";
import { useRouterHook } from "./useRouterHook";
import { appNotification } from "../util/appNotification";
import { isPIN } from "../util/pin";
import { setDashboardOutdateTabsAll } from "../redux/Dashboard/dashboard.slice";

/**
 * App-wide facility switch: persists the selection (with the user's
 * notification preferences), then reboots the app state — in-place via
 * ``populateAppData`` on the Circadia Android shell, via a full page reload
 * everywhere else.
 *
 * Extracted from ``HeaderWeb`` so surfaces that hide the global header's
 * selector (e.g. Neo's Copilot landing) can offer the same behavior.
 */
export const useChangeFacility = () => {
  const dispatch = useAppDispatch();
  const { clinicList, notificationSettings } = useAppSelector((state) => ({
    clinicList: state.clinic.clinicList,
    notificationSettings: state.settings.notifications,
  }));

  const { populateAppData } = useRouterHook({
    amplifyProvider: amplifyProvider,
    authController: authController,
    clinicController: clinicController,
    localStorageProvider: localStorageProvider,
    notificationController: notificationController,
    watchlistController: watchlistController,
    timeProvider: timeProvider,
    appNotification: appNotification,
    patientController: patientController,
    circadiaEngineProvider: circadiaEngineProvider,
    summaryController: summaryController,
    homeHealthController: homeHealthController,
    isPIN: isPIN,
    smsController: smsController,
    userController: userController,
  });

  const changeFacility = async (clinicId: number) => {
    const selectedClinic = await clinicController.setSelectedClinic({
      id: clinicId,
      clinicList,
      notificationSettings: {
        email: notificationSettings?.email || false,
        pushNotification: notificationSettings?.pushNotification || false,
        kioskMode: notificationSettings?.kioskMode || false,
        escalatedAlertPreference:
          notificationSettings?.escalatedAlertPreference || [],
        facilitiesToSendNotificationForOldId: [clinicId],
        psnAlertPreference: notificationSettings?.psnAlertPreference || [],
        bedExitPreference: notificationSettings?.bedExitPreference || [],
        sms: notificationSettings?.sms || false,
        smsPreferences: notificationSettings?.smsPreferences || {
          morning_report: false,
          afternoon_report: false,
          evening_report: false,
          just_in_time: false,
        },
      },
    });
    if (selectedClinic) {
      localStorageProvider.remove("dashboard_filters");
      const isCircadia = circadiaEngineProvider.isWindowCircadia();
      const isAndroid = circadiaEngineProvider.plat === "android";
      if (isCircadia && isAndroid) {
        dispatch(setDashboardOutdateTabsAll());
        populateAppData(true);
      } else {
        window.location.reload();
      }
    }
  };

  return changeFacility;
};
